import { Link, useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import logo from "../assets/logo_basic.png";
import { useAuth } from "../context/AuthProvider";
import AppFooter from "../components/AppFooter";


const editorialPolicySchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": "https://www.reignitespirit.com/editorial-policy#webpage",
  url: "https://www.reignitespirit.com/editorial-policy",
  name: "Editorial Policy",
  description:
    "Editorial standards for Reignite Spirit career intelligence articles and educational content.",
  isPartOf: {
    "@id": "https://www.reignitespirit.com/#website",
  },
};

export default function EditorialPolicyPage() {
  const navigate = useNavigate();
  const { session } = useAuth();

  return (
    <div className="min-h-screen bg-[#0F0A24] text-white">
      <SEO
        title="Editorial Policy"
        description="Read the editorial standards used for Reignite Spirit career intelligence articles, educational resources, reviews, corrections and AI-assisted content."
        path="/editorial-policy"
        image="/og-editorial-policy.jpg"
        type="website"
        author="Reignite Spirit"
        keywords="Reignite Spirit editorial policy, career content standards, article review policy, AI content policy"
        schema={editorialPolicySchema}
      />

      {/* =====================================================
          MAIN WEBSITE HEADER
      ===================================================== */}
      <header
        className="
          sticky
          top-0
          z-50
          border-b
          border-slate-800
          bg-[#11111f]/95
          backdrop-blur
        "
      >
        <div
          className="
            max-w-7xl
            mx-auto
            px-4
            sm:px-6
            lg:px-8
            py-3
            flex
            items-center
            justify-between
            gap-4
          "
        >
          {/* LOGO */}
          <button
            type="button"
            onClick={() => navigate("/landing")}
            className="flex items-center gap-3 cursor-pointer text-left shrink-0"
            aria-label="Go to Reignite Spirit home page"
          >
            <img
              src={logo}
              alt="Reignite Spirit logo"
              className="w-10 h-10 object-contain"
              width="40"
              height="40"
            />

            <div>
              <div className="text-[#EB6AB5] font-semibold">
                REIGNITE.SPIRIT
              </div>

              <p className="text-xs text-[#EB6AB5]/80 uppercase tracking-widest">
                Rekindle Purpose
              </p>
            </div>
          </button>

          {/* DESKTOP NAVIGATION */}
          <nav className="hidden lg:flex items-center gap-5 text-sm">
            <Link
              to="/landing"
              className="text-white/80 hover:text-[#EB6AB5] transition"
            >
              Home
            </Link>

            <Link
              to="/snapshot"
              className="text-white/80 hover:text-[#EB6AB5] transition"
            >
              Snapshot
            </Link>

            <Link
              to="/deep-audit"
              className="text-white/80 hover:text-[#EB6AB5] transition"
            >
              Deep Audit
            </Link>

            <Link
              to="/blog"
              className="text-white/80 hover:text-[#EB6AB5] transition"
            >
              Career Intelligence
            </Link>

            <Link
              to="/faq"
              className="text-white/80 hover:text-[#EB6AB5] transition"
            >
              FAQ
            </Link>

            {session ? (
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="
                  px-4
                  py-2
                  rounded-lg
                  border
                  border-white/20
                  text-white
                  hover:bg-white/10
                  transition
                "
              >
                Dashboard
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="
                    px-3
                    py-2
                    rounded-lg
                    border
                    border-white/20
                    text-white
                    hover:bg-white/10
                    transition
                  "
                >
                  Login
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/register")}
                  className="
                    px-3
                    py-2
                    rounded-lg
                    border
                    border-white/20
                    text-white
                    hover:bg-white/10
                    transition
                    whitespace-nowrap
                  "
                >
                  Register
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/choose-plan")}
                  className="
                    px-3
                    py-2
                    rounded-lg
                    bg-[#cb01bd]
                    hover:bg-[#e94ce0]
                    text-white
                    font-bold
                    transition
                    whitespace-nowrap
                  "
                >
                  Get My Audit
                </button>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* =====================================================
          EDITORIAL POLICY CONTENT
      ===================================================== */}
      <main className="px-6 py-16">
        <article className="max-w-4xl mx-auto">
          <header className="text-center mb-14">
            <p className="text-[#EB6AB5] font-bold uppercase tracking-[0.25em] text-sm">
              Editorial Policy
            </p>

            <h1 className="text-4xl md:text-6xl font-extrabold mt-4">
              Standards for published content
            </h1>

            <p className="mt-6 max-w-3xl mx-auto text-lg text-white/70 leading-relaxed">
              This policy explains the principles used when creating, reviewing,
              updating and correcting educational content published by Reignite
              Spirit.
            </p>
          </header>

          <div className="space-y-6">
            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                Purpose of our content
              </h2>

              <p className="text-white/70 leading-relaxed">
                Our articles and guides are intended to help readers understand
                career change, automation risk, transferable skills,
                labour-market developments, job-search preparation and
                financial transition planning.
              </p>
            </section>

            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                Accuracy and clarity
              </h2>

              <p className="text-white/70 leading-relaxed">
                We aim to distinguish facts, interpretation, estimates and
                opinion. Important claims should be supported by relevant
                sources where practical, and uncertainty should be communicated
                rather than hidden.
              </p>
            </section>

            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                Human review and AI-assisted content
              </h2>

              <p className="text-white/70 leading-relaxed">
                Artificial intelligence tools may be used to support research,
                outlining, editing, summarization or drafting. AI-generated or
                AI-assisted material should be reviewed before publication for
                relevance, clarity, unsupported claims and potentially harmful
                guidance.
              </p>
            </section>

            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                Financial, legal and employment topics
              </h2>

              <p className="text-white/70 leading-relaxed">
                Content involving employment rights, tax, immigration,
                benefits, severance, gratuity or financial planning should
                clearly state that rules vary by jurisdiction and personal
                circumstances. Readers should be encouraged to verify important
                matters with qualified advisers or official sources.
              </p>
            </section>

            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                Corrections and updates
              </h2>

              <p className="text-white/70 leading-relaxed">
                Content may be corrected, expanded or updated when errors,
                omissions or material changes are identified. Publication and
                update dates should be displayed where supported by the page or
                content system.
              </p>
            </section>

            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                Contributors and submitted articles
              </h2>

              <p className="text-white/70 leading-relaxed">
                Submitted content may be reviewed for relevance, originality,
                quality, accuracy, tone and conflicts of interest. Submission
                does not guarantee publication, and content may be edited or
                rejected.
              </p>
            </section>

            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                Commercial relationships
              </h2>

              <p className="text-white/70 leading-relaxed">
                Sponsored, affiliate or commercially influenced content should
                be identified clearly when applicable. Commercial relationships
                should not be presented as independent editorial conclusions.
              </p>
            </section>

            <section className="rounded-3xl border border-[#EB6AB5]/30 bg-[#EB6AB5]/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                Report a concern
              </h2>

              <p className="text-white/70 leading-relaxed">
                Readers who identify a factual error, outdated information or a
                disclosure concern may contact Reignite Spirit for review.
              </p>

              <Link
                to="/contact"
                className="inline-block mt-5 px-5 py-3 rounded-full bg-[#EB6AB5] text-[#0F0A24] font-bold hover:bg-pink-400 transition"
              >
                Contact us
              </Link>
            </section>
          </div>
        </article>
      </main>
      <AppFooter
  variant="landing"
  logoSrc={logo}
/>
    </div>
  );
}